import { forwardRef, useImperativeHandle, useRef } from 'react'
import { StickyBottomNav, StickyBottomNavProps } from '@/ui'
import { mtc } from '@/styles'
import { useHavenTriggerClearance } from './useHavenTriggerClearance'
import { useAdviserPanelInset } from './useAdviserPanelInset'

/**
 * Onboarding's Back/Continue(/Skip) footer. Wraps the generic
 * `StickyBottomNav` design-system primitive with the default every
 * onboarding screen needs: `mx-auto` centering so the bar tracks the same
 * centered column as the page's own content — matching its max-width (via
 * `className`) keeps the divider and buttons from ever rendering flush
 * against the sidebar, since the centered box then naturally falls well
 * short of the available width. Defaults to `max-w-3xl` (this session's
 * plan-pricing page); callers with a narrower content column (e.g. intake's
 * `max-w-2xl`) should override it via `className`.
 *
 * Below `sm` the bar sits at the end of the page rather than pinned to the
 * viewport, so a small screen gives its whole height to the content and the
 * buttons are reached by scrolling. Pass `unfixedOnMobile={false}` for a flow
 * that wants it pinned everywhere.
 *
 * Where Haven's chat trigger is on screen it paints above this bar, so
 * `useHavenTriggerClearance` measures the overlap and pads the bar out from
 * under it — right where the buttons sit at its end, upward once they stack
 * full width. It measures zero where the trigger is suppressed.
 *
 * The trigger is cleared with padding, which moves only the buttons. An open panel is
 * different: it is an in-flow sibling holding real width, so the bar itself has to end at
 * its edge via `rightOffset` or its background runs on underneath.
 *
 * Kept here rather than in `ui` so the design-system primitive doesn't need
 * to know about either.
 */
export const OnboardingStickyNav = forwardRef<
  HTMLDivElement,
  StickyBottomNavProps
>(({ className, style, unfixedOnMobile = true, ...props }, ref) => {
  const barRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(ref, () => barRef.current as HTMLDivElement)
  // An open panel is an in-flow sibling, so the bar itself has to end at its edge. Padding
  // would only move the buttons and leave the background running underneath.
  const panelInset = useAdviserPanelInset()
  const rightOffset = panelInset || props.rightOffset
  const trigger = useHavenTriggerClearance(barRef, {
    left: props.leftOffset,
    right: rightOffset,
  })
  // A bar that scrolls with the page meets the trigger the way the rest of the page does,
  // and clearance measured against the viewport would not track the scroll in any case.
  const haven = unfixedOnMobile ? { ...trigger, bottom: 0 } : trigger
  return (
    <StickyBottomNav
      ref={barRef}
      {...props}
      unfixedOnMobile={unfixedOnMobile}
      rightOffset={rightOffset}
      className={mtc('mx-auto max-w-3xl px-6', className)}
      style={{
        ...(haven.right ? { paddingRight: haven.right } : {}),
        ...(haven.bottom ? { paddingBottom: haven.bottom } : {}),
        ...style,
      }}
    />
  )
})

OnboardingStickyNav.displayName = 'OnboardingStickyNav'
